import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserPlus, LogIn, Trophy, Users, Image as ImageIcon, ClipboardList, X } from 'lucide-react';

/**
 * One-shot popup shown to anonymous users right after the guest wizard
 * generates their schedule. Nudges them to create an account or sign in
 * so they unlock scores, logos, rosters, and standings.
 *
 * Dismissal is tracked in sessionStorage keyed by the event id so we
 * don't nag them on every refresh of the same page.
 */
export function AnonSignupPromoModal({ eventId, onDismiss }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    const key = `sg_anon_promo_dismissed:${eventId}`;
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(key)) return;
    // Small delay lets the schedule paint first so the modal feels like
    // a celebratory pop rather than a wall in front of the app.
    const t = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(t);
  }, [eventId]);

  const dismiss = () => {
    if (typeof window !== 'undefined' && eventId) {
      sessionStorage.setItem(`sg_anon_promo_dismissed:${eventId}`, '1');
    }
    setVisible(false);
    onDismiss?.();
  };

  if (!visible) return null;

  const stashEventId = () => {
    // Persist across the login/register round-trip so AuthContext can
    // claim it right after signup (see AuthContext.claimPendingGuestEvent).
    if (typeof window !== 'undefined' && eventId) {
      try { localStorage.setItem('sg_guest_event_id', eventId); } catch { /* private mode */ }
    }
  };

  const goSignup = () => {
    stashEventId();
    dismiss();
    navigate('/login?mode=register');
  };
  const goLogin = () => {
    stashEventId();
    dismiss();
    navigate('/login');
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="anon-signup-promo"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <Card className="w-full max-w-md border-amber-300 shadow-2xl overflow-hidden">
        <CardHeader className="border-b bg-gradient-to-br from-amber-50 to-white relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={dismiss}
            className="absolute right-2 top-2 h-7 w-7 text-slate-500 hover:text-slate-800"
            data-testid="anon-promo-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </Button>
          <div className="flex items-start gap-3 pr-6">
            <div className="w-11 h-11 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
              <Trophy className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-slate-900 text-lg leading-tight">
                Your schedule is live — take it further
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1.5">
                Want to enter scores, display team logos, rosters and standings?
                Create a free account or sign in and this schedule stays with you.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-5 pb-5 space-y-4">
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <ClipboardList className="w-4 h-4" />
              </span>
              Enter scores + track live standings automatically
            </li>
            <li className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <ImageIcon className="w-4 h-4" />
              </span>
              Display team logos on your printed and shared schedule
            </li>
            <li className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4" />
              </span>
              Manage rosters, contacts, and share with your teams
            </li>
          </ul>
          <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-1">
            <Button
              onClick={goSignup}
              className="flex-1 bg-[#00247D] hover:bg-[#001A5C] text-white"
              data-testid="anon-promo-signup-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Create free account
            </Button>
            <Button
              onClick={goLogin}
              variant="outline"
              className="flex-1"
              data-testid="anon-promo-login-btn"
            >
              <LogIn className="w-4 h-4 mr-2" /> Sign in
            </Button>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="w-full text-center text-xs text-slate-500 hover:text-slate-700 pt-1"
            data-testid="anon-promo-later-btn"
          >
            Keep browsing without an account
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
